// The only module that knows an API URL or the admin password.
//
// Before this existed, `/api/addupdate` was hardcoded in six components and
// the password was read from localStorage at sixteen call sites in four
// different transports. Everything now goes through here, so repointing the
// admin at a different backend is one env var and the auth rules live in one
// place.

import axios from 'axios';
import { sanitize } from './schema';

const LEARN = process.env.REACT_APP_LEARN_API || 'https://learn.philspeiser.com';
const SERVE = process.env.REACT_APP_SERVE_API || 'https://serve.philspeiser.com';
const GEN = process.env.REACT_APP_GEN_API || 'https://gen.philspeiser.com';

export const ENDPOINTS = { LEARN, SERVE, GEN };

const JSON_HEADERS = { 'Content-Type': 'application/json' };
const PASSWORD_KEY = 'password';

// ---------------------------------------------------------------- auth

export function getPassword() {
  return localStorage.getItem(PASSWORD_KEY) || '';
}

export function setPassword(value) {
  localStorage.setItem(PASSWORD_KEY, value);
}

export function clearPassword() {
  localStorage.removeItem(PASSWORD_KEY);
}

// ---------------------------------------------------------- error shaping

// The generation pipelines return errors as {"detail": ...}, where `detail` is
// sometimes the payload and sometimes just a string.
export function unwrap(data) {
  return data && typeof data.detail === 'object' && data.detail !== null ? data.detail : data;
}

export function messageOf(data) {
  const payload = unwrap(data);
  return payload?.error || payload?.message || (typeof data?.detail === 'string' ? data.detail : '');
}

// One phrasing of "that didn't work", so the four hand-rolled copies of
// `error.response?.status === 401` cannot drift apart. `consequence` says what
// the failure left behind -- the part an admin actually needs.
export function describeError(error, consequence = 'Nothing was changed.') {
  const status = error?.response?.status;
  if (status === 401) return `Wrong password. ${consequence}`;
  const detail = messageOf(error?.response?.data);
  if (detail) return `${detail} ${consequence}`;
  if (status) return `The server returned ${status}. ${consequence}`;
  return `${error?.message || 'Request failed'}. ${consequence}`;
}

// ---------------------------------------------------------------- catalog

// getall returns every key in the KV namespace, not only video records, and it
// returns them exactly as stored -- including any field that should never have
// been written there.
export async function getAllRaw() {
  const { data } = await axios.get(`${LEARN}/api/getall`);
  const records = {};
  for (const [identifier, record] of Object.entries(data || {})) {
    if (!record || typeof record !== 'object' || Array.isArray(record)) continue;
    records[identifier] = record;
  }
  return records;
}

export async function getAll() {
  const raw = await getAllRaw();
  return Object.fromEntries(Object.entries(raw).map(([id, r]) => [id, sanitize(r)]));
}

export async function getVideo(identifier) {
  const { data } = await axios.get(`${LEARN}/api/getvideo`, { params: { identifier } });
  return sanitize(data);
}

// addupdate merges, so a patch only has to carry what changed. Sending the
// whole record -- which is what the old admin did -- means every save races
// every other save on every field.
export async function patchVideo(identifier, patch) {
  const { data } = await axios.post(
    `${LEARN}/api/addupdate`,
    { ...sanitize(patch), identifier, password: getPassword() },
    { headers: JSON_HEADERS },
  );
  return sanitize(data.data);
}

// Removing a key needs an explicit channel: because addupdate merges, simply
// omitting a field from the patch leaves it in place. Sending the record
// without it -- the old approach -- was a silent no-op.
export async function deleteFields(identifier, keys) {
  const { data } = await axios.post(
    `${LEARN}/api/addupdate`,
    { identifier, password: getPassword(), __delete: keys },
    { headers: JSON_HEADERS },
  );
  return sanitize(data.data);
}

export async function deleteVideo(identifier) {
  await axios.delete(`${LEARN}/api/deletevideo`, {
    params: { identifier, password: getPassword() },
  });
}

// ------------------------------------------------------------- thumbnails

// Uploads the image and returns its URL. Which field that URL belongs in --
// `thumbnailUrl` for the poster, `minithumbnailUrl` for the grid card -- is
// the caller's decision, made with a separate patchVideo.
export async function uploadThumbnail(identifier, blob, filename = 'thumbnail.png') {
  const formData = new FormData();
  formData.append('file', blob, filename);
  formData.append('identifier', identifier);
  formData.append('password', getPassword());

  const { data } = await axios.put(`${SERVE}/api/uploadthumbnail`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data.url;
}

// -------------------------------------------------------------- pipelines

// Both pipelines answer with meaningful non-2xx statuses -- 422 is a gate the
// admin is meant to read and override, not an error -- so these return the
// status rather than throwing on it. Correction plus four translations runs
// well past any default timeout.
const PIPELINE_CONFIG = {
  headers: JSON_HEADERS,
  timeout: 0,
  validateStatus: () => true,
};

export async function subtitlePipeline({ identifier, vtt, force = false }) {
  const { status, data } = await axios.post(
    `${GEN}/ask/chat/subtitlepipeline`,
    { password: getPassword(), identifier, vtt, register: true, force },
    PIPELINE_CONFIG,
  );
  return { status, data };
}

// The registered tracks are public, CORS-open blobs, so the English one can be
// read back out of the record. That is what lets the admin fill in a missing
// translation without hunting down the original transcription again.
//
// axios parses a response that looks like JSON, and a VTT whose first cue is a
// bare number is close enough to trip that -- the identity transform keeps the
// text as text.
export async function fetchSubtitleText(src) {
  const { data } = await axios.get(src, {
    responseType: 'text',
    transformResponse: [(value) => value],
  });
  return typeof data === 'string' ? data : '';
}

export async function chapterPipeline({ identifier, register, force = false }) {
  const { status, data } = await axios.post(
    `${GEN}/ask/chat/chapterpipeline`,
    { password: getPassword(), identifier, register, force },
    PIPELINE_CONFIG,
  );
  return { status, data };
}

// ------------------------------------------------------------------ order

// The order service reads with the password in a header and writes with it in
// the body. That asymmetry is the server's, not ours.
export async function getOrder() {
  const { data } = await axios.get(`${SERVE}/api/getvideoorder`, {
    headers: { 'x-edit-password': getPassword() },
  });
  return Array.isArray(data) ? data : [];
}

export async function setOrder(order) {
  await axios.patch(`${SERVE}/api/updatevideoorder`, {
    password: getPassword(),
    value: order,
  });
}
